import { useState, useCallback } from 'react';
import { Mui } from '@osu/react-ui';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSubmitScholarsRecommendationMutation } from '../../services/graphql/api';
import { selector as rubricValuesSelector } from '../slices/rubricValues';
import { selector as reviewNotesSelector, getSubmittableNotes, hasUncheckedChildren } from '../slices/administrativeNotes';
import { isRubricComplete } from './recommendationSubmitValidation';
import { useApplicantDetailsState } from '../hooks/useApplicantDetailsState';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';
import { canDecideScholars } from '../../util/scholars/scholarsScope';
import { DECISION_LABELS } from '../../util/constants';

// has503 — Record a Scholars Admit/Deny decision (E3). Mirrors the Honors recommendation UX:
// a button group of outcomes → Submit → confirmation dialog. Scholars uses "Decision" wording
// (Honors uses "Recommendation"). The final admin approval is a separate step (recordScholarsDecision).
// Backend enum values are RECOMMEND_ADMIT / RECOMMEND_DENY; the UI labels them Admit / Deny.
// Derived from the shared DECISION_LABELS map (util/constants.js) so wording stays in one place.
const OUTCOMES = Object.entries(DECISION_LABELS).map(([key, label]) => ({ key, label }));

const RUBRIC_INCOMPLETE_MESSAGE = 'You must select a grade value for all rating items before submitting a decision.';

const labelFor = (key) => OUTCOMES.find((o) => o.key === key)?.label ?? key;

export default function ScholarsDecisionPanel({ applicantEmployeeId, subProgramId, term, programName }) {
    const { roles = [] } = useRoleAuthentication();
    const navigate = useNavigate();
    const [submitRecommendation, { isLoading }] = useSubmitScholarsRecommendationMutation();
    const rubricValues = useSelector(rubricValuesSelector);
    // has600 — the reviewer's checked admin-note selections (See Essay, New Student Support, etc.). Sent on
    // submit so they persist as a ReviewNotes entity and surface in the Admin Notes report (Honors parity).
    const reviewNotes = useSelector(reviewNotesSelector);
    const missingChildNodes = hasUncheckedChildren(reviewNotes);
    // Route-aware applicant details carry configuration.rubric (same source the Ratings section renders
    // from). Used to tell "a rubric is displayed" (configured) from "no rubric configured".
    const { data: appDetails } = useApplicantDetailsState();
    const [selected, setSelected] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [result, setResult] = useState('');

    const canDecide = canDecideScholars(roles);

    // Requirement rubric-completeness-requirements.md (FR-RC-1/2/3): when the Ratings (rubric) section
    // is displayed for this applicant, every rubric item must be selected before a decision can be
    // submitted. A rubric is "displayed" when the active config has at least one rubric item. If no
    // rubric is configured, there is nothing to complete, so the decision is not blocked (FR-RC-3).
    const rubricIsConfigured = (appDetails?.configuration?.rubric?.rubricItems?.length ?? 0) > 0;
    const rubricBlocksSubmit = rubricIsConfigured && !isRubricComplete(rubricValues);
    const hasBlockingSubmissionErrors = rubricBlocksSubmit || missingChildNodes;

    const onConfirm = useCallback(async () => {
        setResult('');
        try {
            // Persist the reviewer's Ratings alongside the decision so they can be shown read-only after
            // submission. AWSJSON expects a JSON string; the rubric slice is { rubricKey, "<label>": "<choice>" }.
            await submitRecommendation({
                applicantEmployeeId,
                subProgramId,
                term,
                recommendation: selected,
                rubricValues: JSON.stringify(rubricValues ?? {}),
                // has600 — AWSJSON array of { label, parentLabel, section } for the checked admin notes.
                administrativeNotes: JSON.stringify(getSubmittableNotes(reviewNotes ?? [])),
            }).unwrap();
            setDialogOpen(false);
            // Kick back to the program queue to pull the next applicant. A DENY advances the cascade
            // asynchronously (EventBridge -> U6 places the next-preference QUEUED entry out-of-band), so
            // signal the queue to do delayed refetches — otherwise the destination program's "New" count
            // stays stale until a manual refresh (has561 bugfix). Admits don't advance the cascade, so only
            // flag on a deny.
            navigate(`/scholars/queue/${subProgramId}`, {
                state: { cascadeAdvanced: selected === 'RECOMMEND_DENY' },
            });
        } catch (err) {
            setResult('Unable to record the decision. Please try again.');
            setDialogOpen(false);
        }
    }, [selected, submitRecommendation, applicantEmployeeId, subProgramId, term, navigate, rubricValues, reviewNotes]);

    // Guarded open: never open the confirm dialog when the displayed rubric is incomplete.
    const onSubmitClick = useCallback(() => {
        if (hasBlockingSubmissionErrors) {
            setResult(RUBRIC_INCOMPLETE_MESSAGE);
            return;
        }
        setResult('');
        setDialogOpen(true);
    }, [hasBlockingSubmissionErrors]);

    if (!canDecide) { return null; }

    return (
        <Mui.Box sx={{ mt: 2 }} data-testid="scholars-decision-panel">
            <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Mui.ButtonGroup aria-label="Scholars decision options">
                    {OUTCOMES.map((o) => {
                        const isSelected = selected === o.key;
                        return (
                            <Mui.Button
                                key={o.key}
                                onClick={() => setSelected((val) => (val === o.key ? '' : o.key))}
                                color={isSelected ? 'secondary' : undefined}
                                variant={isSelected ? 'contained' : undefined}
                                data-testid={`scholars-decision-${o.label.toLowerCase()}`}
                            >
                                {o.label}
                            </Mui.Button>
                        );
                    })}
                </Mui.ButtonGroup>
                <Mui.Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmitClick}
                    disabled={!selected || isLoading || rubricBlocksSubmit || missingChildNodes}
                    data-testid="scholars-decision-submit"
                >
                    Submit
                </Mui.Button>
            </Mui.Box>

            {rubricBlocksSubmit ? (
                <Mui.Typography
                    sx={{ mt: 1, color: 'error.main' }}
                    data-testid="scholars-decision-rubric-error"
                    tabIndex={0}
                >
                    {RUBRIC_INCOMPLETE_MESSAGE}
                </Mui.Typography>
            ) : null}

            {missingChildNodes && <Mui.Typography sx={{ mt: 1, color: 'error.main' }}>You must select a subfield in Administrative Notes before clicking submit.</Mui.Typography> }

            <Mui.Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} aria-labelledby="scholars-decision-dialog-title">
                <Mui.DialogTitle id="scholars-decision-dialog-title">Submit Decision</Mui.DialogTitle>
                <Mui.DialogContent>
                    <Mui.Typography>
                        Record a decision of <strong>{labelFor(selected)}</strong>
                        {programName ? <> for <strong>{programName}</strong></> : null}?
                    </Mui.Typography>
                </Mui.DialogContent>
                <Mui.DialogActions>
                    <Mui.Button onClick={() => setDialogOpen(false)} disabled={isLoading}>Cancel</Mui.Button>
                    <Mui.Button onClick={onConfirm} variant="contained" disabled={isLoading || rubricBlocksSubmit} data-testid="scholars-decision-confirm">
                        {isLoading ? 'Submitting…' : 'Submit Decision'}
                    </Mui.Button>
                </Mui.DialogActions>
            </Mui.Dialog>

            <Mui.Box aria-live="polite" role="status" sx={{ mt: 1 }}>{result}</Mui.Box>
        </Mui.Box>
    );
}
